import { EVENT_MAP, EventsName } from "@/configs/events.config";
import mixpanel from "mixpanel-browser";

import { useQueryClient } from "@tanstack/react-query";
import { User } from "@/types/user.types";

// import { useAnalytics } from "~/providers";
// import { UserDetails } from "~/types/userDetails.type";

// import { UserProperties } from "./analytics.type";

type Services = "mixpanel" | "clevertap" | "all";

const MIXPANEL_TOKEN = import.meta.env.VITE_PUBLIC_MIXPANEL_TOKEN;
const MODE = import.meta.env.MODE;

// Mixpanel is only usable once initialized with a real token. Without a token,
// init() leaves the lib half-configured and any track() call throws
// ("Cannot read properties of undefined (reading 'before_track')"), which would
// crash the React tree. Gate every call on this flag so analytics no-ops cleanly.
let analyticsEnabled = false;

export const initAnalytics = () => {
  if (!MIXPANEL_TOKEN) {
    if (MODE === "development") {
      console.warn(
        "[analytics] VITE_PUBLIC_MIXPANEL_TOKEN is not set; analytics disabled."
      );
    }
    return;
  }

  mixpanel.init(MIXPANEL_TOKEN, {
    debug: MODE === "development",
    ignore_dnt: true,
  });
  analyticsEnabled = true;
};

const useAnalyticsManager = () => {
  const queryClient = useQueryClient();
  const user = queryClient.getQueryData<User>(["user"]);
  // const { data: user } = useGetUserQuery();

  const identifyUser = (identity: string) => {
    if (!analyticsEnabled) return;
    /** Mixpanel */
    mixpanel.identify(identity);

    /** Clevertap */
  };

  const setUserProperties = (
    firstName: string,
    lastName: string,
    email: string,
    roles: string[] = [],
    tenantName?: string,
    tenantId?: number
  ) => {
    if (!analyticsEnabled) return;
    mixpanel.people.set({
      $first_name: firstName,
      $last_name: lastName,
      $email: email,
      $roles: roles,
      $tenantName: tenantName,
      $tenantId: tenantId,
    });
  };

  const sendMixpanelEvent = (
    eventName: EventsName,
    extraEventProps: Record<string, string | number | boolean>
  ) => {
    if (!analyticsEnabled) return;
    mixpanel.track(eventName, {
      ...EVENT_MAP[eventName],
      ...extraEventProps,
    });
  };

  const sendEvent = (
    eventName: EventsName,
    payload: Record<string, string | number | boolean> = {},
    services: Services[] = ["mixpanel"]
  ) => {
    const { id } = user || {};
    if (services.includes("all") || services.includes("mixpanel")) {
      sendMixpanelEvent(eventName, { ...payload, userId: id! });
    }
  };

  const resetUser = () => {
    if (!analyticsEnabled) return;
    mixpanel.reset();
  };

  return {
    sendEvent,
    resetUser,
    initAnalytics,
    identifyUser,
    setUserProperties,
  };
};

export default useAnalyticsManager;
